import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
} from "antd";
import { useEffect, useState } from "react";
import Header from "../components/Header";
import {
  createDefaultProduct,
  deleteDefaultProduct,
  getDefaultProducts,
  updateDefaultProduct,
} from "../repository/defaultProductApi";
import apiService from "../services/apiService";

// --- Dropdown APIs ---
const loginObj = JSON.parse(localStorage.getItem("auth")) || {};

export const getProducts = async () => {
  const res = await apiService.get(
    `/accountingapi/GetProduct/?org=${loginObj.org}&is_archive=false`,
  );
  return res;
};

export const getServices = async () => {
  const res = await apiService.get(
    `/productsapi/products/?org=${loginObj.org}&is_archive=false`,
  );
  return res;
};

const DefaultProducts = () => {
  const [productOptions, setProductOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
    fetchDropdowns();
  }, []);

  const fetchDropdowns = async () => {
    try {
      const [productsRes, servicesRes] = await Promise.all([
        getProducts(),
        getServices(),
      ]);

      const products = productsRes?.data || productsRes || [];
      const services = servicesRes?.data || servicesRes || [];

      setProductOptions(
        products.map((item) => ({
          label: item.ProductName,
          value: item.id,
        })),
      );

      setServiceOptions(
        services.map((item) => ({
          label: item.title,
          value: item.id,
        })),
      );
    } catch (err) {
      message.error("Failed to load dropdowns");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getDefaultProducts();
      setData(res?.data || res || []);
    } catch (err) {
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingRecord) {
        const updated = await updateDefaultProduct(editingRecord.id, values);
        setData((prev) =>
          prev.map((item) =>
            item.id === editingRecord.id ? { ...item, ...updated } : item,
          ),
        );
        message.success("Updated successfully");
      } else {
        const created = await createDefaultProduct(values);
        setData((prev) => [...prev, created]);
        message.success("Created successfully");
      }

      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
    } catch (err) {
      message.error("Something went wrong");
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDefaultProduct(id);
      setData((prev) => prev.filter((item) => item.id !== id));
      message.success("Deleted successfully");
    } catch (err) {
      message.error("Delete failed");
    }
  };

  const columns = [
    {
      title: "Product Name",
      dataIndex: ["product_key", "ProductName"],
    },
    {
      title: "Type",
      dataIndex: ["product_key", "ProductType"],
    },
    {
      title: "Price",
      dataIndex: ["product_key", "SalesPrice"],
    },
    {
      title: "Service",
      dataIndex: ["service_key", "title"],
    },
    {
      title: "Service Desc",
      dataIndex: ["service_key", "description"],
      render: (text) => text || "-",
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />

          <Popconfirm
            title="Are you sure to delete this product?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      <Header />

      <div style={{ flex: "11 11 100%", padding: "20px", marginTop: "64px" }}>
        {/* Header Row */}
        <div className="row">
          <div className="col-6">
            <h1 className="text-2xl font-bold">Default Products</h1>
          </div>
          <div className="col-6">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ float: "right" }}
              onClick={() => {
                setEditingRecord(null);
                form.resetFields();
                setModalOpen(true);
              }}
            >
              Add Product
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="row p-4">
          <div className="col-12 r-shadow">
            <Table
              columns={columns}
              dataSource={data}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </div>
        </div>

        {/* Modal */}
        <Modal
          title={editingRecord ? "Edit Product" : "Add Product"}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onOk={handleSubmit}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="product_key_id"
              label="Default Product"
              rules={[{ required: true, message: "Select product" }]}
            >
              <Select
                showSearch
                placeholder="Select Product"
                optionFilterProp="label"
                options={productOptions}
              />
            </Form.Item>

            <Form.Item
              name="service_key_id"
              label="Platform"
              rules={[{ required: true, message: "Select service" }]}
            >
              <Select
                showSearch
                placeholder="Select Service"
                optionFilterProp="label"
                options={serviceOptions}
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default DefaultProducts;
